import { AssignmentDTO, AssignmentDTOClient, LessThanRequiredVisualsReason, StarRatingEntry } from 'models/assignment'
import { CORAL_600, GRAY_700, GRAY_900, GREEN_600, YELLOW_600 } from 'constants/styling/theme'
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { SELF_UPLOAD_PRODUCT_IDS, SelfUploadProductCountryCodeMap } from 'constants/product'
import { useDynamicNow, useStarRatingEntries } from 'utils/hooks'

import { AssignmentDTOIsClientDTO } from 'utils/typeguards/assignmentTypeguards'
import { AssignmentDetailDrawer } from '../AssignmentDetailDrawer'
import { AssignmentStage } from 'constants/assignment'
import { DealState } from 'constants/deal'
import { DotDivider } from 'components/common/DotDivider'
import { GalleryInfoCard } from '../GalleryInfoCard/GalleryInfoCard.component'
import { MUIButton } from 'components/common/MUIButton'
import { OrderCancellationPopup } from 'components/common/OrderCancellationPopup'
import { OrderCreator } from 'components/pages/Gallery/_main/ClientGallery/common/OrderCreator/OrderCreator.component'
import { OrderDetailAssignmentItem } from '../OrderDetailAssignmentItem'
import { RescheduleOrderModal } from 'components/common/RescheduleOrderModal'
import Stack from '@mui/material/Stack'
import StarIcon from '@mui/icons-material/Star'
import { StarRatingPopup } from 'components/common/StarRating'
import { SystemMessage } from 'components/common/SystemMessage'
import { TFunction } from 'i18next'
import Typography from '@mui/material/Typography'
import { choosePrimaryProduct } from 'utils/product'
import { compareByShootingStart } from 'utils/time'
import styles from './ClientOrderDetail.module.sass'
import { useGalleryAssignment } from 'components/pages/Gallery/_main/contexts/GalleryAssignment.context'
import { useGalleryDeal } from 'components/pages/Gallery/_main/contexts/GalleryDeal.context'
import { useRescheduleOrderModule } from 'components/common/RescheduleOrder'
import { useStarRatingAnalyticsData } from 'components/pages/Gallery/_main/contexts/useStarRatingAnalyticsData'
import { useTranslation } from 'react-i18next'

/** Displays a message for when there are fewer visuals than required used in the SystemMessage comment prop. */
const LessRequiredVisualsMessage = (
  { reason, comment, t }: { reason: LessThanRequiredVisualsReason, comment?: string, t: TFunction }
) => (
  <Stack>
    <Typography variant="text-sm" fontWeight={500} color={GRAY_900}>
      {t(`enums:less_than_required_visuals_reason.${reason}`)}
    </Typography>

    {comment && <Typography variant="text-sm" color={GRAY_700}> {`"${comment}"`}</Typography>}
  </Stack>
)

/**
 * @interface Props Input properties
 */
interface Props {
  /** OnClick action to open the order detail drawer */
  onOpenDrawer: () => void
  /** Optional children to render inside the order detail */
  children?: ReactNode
}

/**
 * Gallery client order detail component
 * @example <ClientOrderDetail></ClientOrderDetail>
 */
export const ClientOrderDetail: FC<Props> = ({ onOpenDrawer, children }) => {
  const { t } = useTranslation(['gallery', 'enums', 'star_rating'])
  const { dealData, allowCancelOrder } = useGalleryDeal()
  const { isVisualSendToClient, realtyId } = useGalleryAssignment()
  const { isReschedulePossible } = useRescheduleOrderModule()
  const dynamicNow = useDynamicNow()
  const showCancelledLines = useFeatureFlag(FeatureFlag.ENABLE_SF_LAST_LINE_CANCEL)

  const [isCancellationPopupOpen, setIsCancellationPopupOpen] = useState<boolean>(false)
  const [isRescheduleOpen, setIsRescheduleOpen] = useState<boolean>(false)
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null)
  const [ratingAssignmentId, setRatingAssignmentId] = useState<string | null>(null)
  const [localRatingsMap, setLocalRatingsMap] = useState<Record<string, StarRatingEntry[]>>({})
  const [localAvgRating, setLocalAvgRating] = useState<number | null | undefined>(undefined)

  /** Assignments listed by shooting start, undated last (copied, never sorting the query cache) */
  const assignmentsByShootingStart = useMemo(
    () => dealData?.assignments ? [...dealData.assignments].sort(compareByShootingStart) : [],
    [dealData?.assignments]
  )

  const selectedAssignment = useMemo(
    () => dealData?.assignments.find(a => a.id === selectedAssignmentId) ?? null,
    [dealData?.assignments, selectedAssignmentId]
  )

  const showAssignmentDetailDrawer = useCallback((assignment: AssignmentDTO | null) => {
    return !assignment?.products.find((product) => SELF_UPLOAD_PRODUCT_IDS.has(product.id))
  }, [])

  // Resolve the clicked assignment for the rating popup
  const ratingAssignment = useMemo<AssignmentDTOClient | undefined>(() => {
    if (!dealData || !ratingAssignmentId) return undefined
    const found = dealData.assignments.find(a => a.id === ratingAssignmentId)
    return found && AssignmentDTOIsClientDTO(found) ? found as AssignmentDTOClient : undefined
  }, [dealData, ratingAssignmentId])

  // Safety: reset ratingAssignmentId when it can't be resolved (e.g. refetch removed the item)
  useEffect(() => {
    if (ratingAssignmentId && !ratingAssignment) setRatingAssignmentId(null)
  }, [ratingAssignmentId, ratingAssignment])

  const starRatingAnalyticsData = useStarRatingAnalyticsData({
    creativeId: ratingAssignment?.creative?.creativeId ?? null,
    orderId: ratingAssignment?.dealId != null ? String(ratingAssignment.dealId) : null,
    realtyId: ratingAssignment?.property?.id ?? realtyId ?? null
  })

  // Check if rating should be hidden for a given assignment (self-upload, AI editing)
  const isAssignmentRatingHidden = useCallback((assignment: AssignmentDTO): boolean => {
    if (!AssignmentDTOIsClientDTO(assignment)) return true
    const product = choosePrimaryProduct(assignment.products)
    if (!product) return true
    if (product.isAiPhotoEditing) return true
    if (Object.values(SelfUploadProductCountryCodeMap).includes(product.id)) return true
    return false
  }, [])

  // A cancelled line was never delivered, so it is never rated
  const isLineCancelled = useCallback((assignment: Pick<AssignmentDTOClient, 'isCancelled'>): boolean => showCancelledLines && assignment.isCancelled, [showCancelledLines])

  // At least one assignment in the order is ratable (for order-level star display)
  const isAnyAssignmentRatable = useMemo(() => {
    if (!dealData) return false
    return dealData.assignments.some(a => !isAssignmentRatingHidden(a) && !isLineCancelled(a))
  }, [dealData, isAssignmentRatingHidden, isLineCancelled])

  const isOrderCancelled = dealData?.state === DealState.CANCELLED

  // Fetch detailed ratings (with criteria) from the dedicated endpoint
  const { ratings: detailedRatings, isFavouriteCT } = useStarRatingEntries(ratingAssignment?.id)

  // Use local avg rating (from submission response) if available, otherwise fall back to the DTO value
  const avgRating = localAvgRating !== undefined ? localAvgRating : dealData?.avgRating
  const hasRating = avgRating != null
  const averageRating = hasRating ? String(avgRating).replace('.', ',') : undefined

  const ratingStarColor = avgRating === 5 ? GREEN_600 : avgRating != null && avgRating < 2 ? CORAL_600 : YELLOW_600

  // Check whether a specific assignment already has a rating
  const getAssignmentHasRating = useCallback((assignment: AssignmentDTO): boolean => {
    if (localRatingsMap[assignment.id]) return true
    if (!AssignmentDTOIsClientDTO(assignment)) return false
    const ratings = (assignment as AssignmentDTOClient).ratings
    if (!ratings) return false
    return Object.values(ratings).some((r) => r?.rating != null)
  }, [localRatingsMap])

  const handleRatingComplete = useCallback((entries: StarRatingEntry[], responseAvgRating?: number | null) => {
    if (ratingAssignmentId) {
      setLocalRatingsMap(prev => ({ ...prev, [ratingAssignmentId]: entries }))
    }
    if (responseAvgRating !== undefined) setLocalAvgRating(responseAvgRating)
    setRatingAssignmentId(null)
  }, [ratingAssignmentId])

  return (
    <GalleryInfoCard alignItems='left'>
      <Stack className={styles.orderDetail}>

        {/** GENERAL ORDER INFO */}
        <Stack direction={{ xs: 'column', sm: 'row' }} paddingBottom={2} alignItems={{ xs: 'left', sm: 'center' }} justifyContent="space-between">
          <Stack paddingBottom={{ xs: '1rem', sm: 0 }}>
            {/** ORDER ID */}
            <Stack direction="row">
              <span className={styles.title}>{t('order_informations.order')}</span>
              <DotDivider />
              {dealData && <span className={styles.title}>{`#${dealData.id}`}</span>}
            </Stack>

            {/** ORDER CREATOR + INLINE RATING */}
            <OrderCreator verticalAlignment={false}>

              {isVisualSendToClient && isAnyAssignmentRatable && hasRating &&
                <Stack direction="row" alignItems="center" gap=".2rem">

                  <StarIcon sx={{ fontSize: '1.6rem', color: ratingStarColor }} />

                  <Typography
                    variant="text-sm"
                    fontWeight={500}
                    color={GRAY_900}
                  >
                    {averageRating}
                  </Typography>

                </Stack>
              }

            </OrderCreator>
          </Stack>

          <Stack flexDirection="row" gap=".8rem" alignItems="center" flexShrink={0}>

            <MUIButton type='secondaryBorder' onClick={onOpenDrawer} variant='outlined'>{t('order_informations.see_order_details')}</MUIButton>

            {isReschedulePossible &&
              <MUIButton type='secondaryBorder' onClick={() => setIsRescheduleOpen(true)}>
                {t('reschedule')}
              </MUIButton>
            }

            {allowCancelOrder &&
              <MUIButton
                type="orangeSecondaryBorder"
                onClick={() => setIsCancellationPopupOpen(true)}
              >
                {t('actions:Cancel')}
              </MUIButton>
            }

          </Stack>
        </Stack>

        {/** ASSIGNMENTS ITEMS */}
        <Stack gap="1.6rem" direction="column-reverse">
          {!!dealData && assignmentsByShootingStart.map((assignment) => (
            <OrderDetailAssignmentItem
              key={`assignment-${assignment.id}`}
              assignment={assignment}
              orderId={dealData.id}
              onEditRating={!isOrderCancelled && assignment.stage === AssignmentStage.VISUALS_SENT_TO_CLIENT && !isAssignmentRatingHidden(assignment) && !isLineCancelled(assignment)
                ? () => setRatingAssignmentId(assignment.id)
                : undefined
              }
              hasRating={getAssignmentHasRating(assignment)}
              button={showAssignmentDetailDrawer(assignment)
                ? <MUIButton
                  type="secondaryBorder"
                  variant="outlined"
                  onClick={() => setSelectedAssignmentId(assignment.id)}
                >
                  {t('order_informations.see_details')}
                </MUIButton>
                : undefined
              }
              footerContent={assignment.lessThanRequiredVisualsReason && assignment.lessThanRequiredVisualsReason !== LessThanRequiredVisualsReason.DRONE_CANT_DO_BRACKETING &&
                <SystemMessage
                  variant="warning"
                  title={t('less_than_required_visuals_title')}
                  message={
                    <LessRequiredVisualsMessage
                      t={t}
                      reason={assignment.lessThanRequiredVisualsReason}
                      comment={assignment.lessThanRequiredVisualsComment ?? undefined}
                    />
                  }
                />
              }
            />
          ))}
        </Stack>

        {children}

      </Stack>

      {!!dealData &&
        <OrderCancellationPopup
          data={dealData}
          isOpen={isCancellationPopupOpen}
          dynamicNow={dynamicNow}
          onClickClose={() => setIsCancellationPopupOpen(false)}
        />
      }

      <RescheduleOrderModal
        isOpen={isRescheduleOpen}
        onClose={() => setIsRescheduleOpen(false)}
      />

      {showAssignmentDetailDrawer(selectedAssignment) && !!dealData &&
        <AssignmentDetailDrawer
          isOpen={!!selectedAssignment}
          onClose={() => setSelectedAssignmentId(null)}
          assignment={selectedAssignment}
          orderId={dealData.id}
          workspaceId={dealData.property?.workspaceId ?? ''}
          order={dealData}
        />
      }

      {!isOrderCancelled && ratingAssignment?.stage === AssignmentStage.VISUALS_SENT_TO_CLIENT && ratingAssignment && !isLineCancelled(ratingAssignment) &&
        <StarRatingPopup
          isOpen={ratingAssignmentId !== null}
          onClose={() => setRatingAssignmentId(null)}
          assignmentId={ratingAssignment.id}
          hasCreative={!!ratingAssignment.creative}
          creativeName={ratingAssignment.creative?.name}
          initialRatings={detailedRatings ?? localRatingsMap[ratingAssignment.id]}
          initialIsFavouriteCT={isFavouriteCT}
          onComplete={handleRatingComplete}
          analyticsData={starRatingAnalyticsData}
        />
      }

    </GalleryInfoCard>
  )
}
